import { SvelteComponent, init, not_equal, create_slot, update_slot, transition_in, transition_out } from 'svelte/internal';
import { getContext, setContext, onMount } from 'svelte';
import { readable, derived } from 'svelte/store';

var QueryStatus;
(function(QueryStatus2) {
  QueryStatus2["Idle"] = "idle";
  QueryStatus2["Loading"] = "loading";
  QueryStatus2["Error"] = "error";
  QueryStatus2["Success"] = "success";
})(QueryStatus || (QueryStatus = {}));

class CancelledError {
}
const isServer = typeof window === "undefined";
function noop() {
  return void 0;
}
let Console = console || {
  error: noop,
  warn: noop,
  log: noop
};
function setConsole(c) {
  Console = c;
}
function functionalUpdate(updater, input) {
  return typeof updater === "function" ? updater(input) : updater;
}
function stableStringifyReplacer(_key, value) {
  if (typeof value === "function") {
    throw new Error("Cannot stringify non JSON value");
  }
  if (isObject(value)) {
    return Object.keys(value).sort().reduce((result, key) => {
      result[key] = value[key];
      return result;
    }, {});
  }
  return value;
}
function stableStringify(value) {
  return JSON.stringify(value, stableStringifyReplacer);
}
function deepIncludes(a, b) {
  if (a === b) {
    return true;
  }
  if (typeof a !== typeof b) {
    return false;
  }
  if (typeof a === "object") {
    return !Object.keys(b).some((key) => !deepIncludes(a[key], b[key]));
  }
  return false;
}
function isDocumentVisible() {
  if (typeof document === "undefined") {
    return true;
  }
  return [void 0, "visible", "prerender"].includes(document.visibilityState);
}
function isOnline() {
  return navigator.onLine === void 0 || navigator.onLine;
}
function getQueryArgs(args) {
  let queryKey;
  let queryFn;
  let config;
  let options;
  if (isObject(args[0])) {
    queryKey = args[0].queryKey;
    queryFn = args[0].queryFn;
    config = args[0].config;
    options = args[1];
  } else if (isObject(args[1])) {
    queryKey = args[0];
    config = args[1];
    options = args[2];
  } else {
    queryKey = args[0];
    queryFn = args[1];
    config = args[2];
    options = args[3];
  }
  config = config ? {queryKey, ...config} : {queryKey};
  if (queryFn) {
    config = {...config, queryFn};
  }
  return [queryKey, config, options];
}
function replaceEqualDeep(a, b) {
  if (a === b) {
    return a;
  }
  const array = Array.isArray(a) && Array.isArray(b);
  if (array || isPlainObject(a) && isPlainObject(b)) {
    const aSize = array ? a.length : Object.keys(a).length;
    const bItems = array ? b : Object.keys(b);
    const bSize = bItems.length;
    const copy = array ? [] : {};
    let equalItems = 0;
    for (let i = 0; i < bSize; i++) {
      const key = array ? i : bItems[i];
      copy[key] = replaceEqualDeep(a[key], b[key]);
      if (copy[key] === a[key]) {
        equalItems++;
      }
    }
    return aSize === bSize && equalItems === aSize ? a : copy;
  }
  return b;
}
function isObject(a) {
  return a && typeof a === "object" && !Array.isArray(a);
}
function isPlainObject(o) {
  if (!hasObjectPrototype(o)) {
    return false;
  }
  const ctor = o.constructor;
  if (typeof ctor === "undefined") {
    return true;
  }
  const prot = ctor.prototype;
  if (!hasObjectPrototype(prot)) {
    return false;
  }
  if (!prot.hasOwnProperty("isPrototypeOf")) {
    return false;
  }
  return true;
}
function hasObjectPrototype(o) {
  return Object.prototype.toString.call(o) === "[object Object]";
}
function isCancelable(value) {
  return typeof value?.cancel === "function";
}
function isError(value) {
  return value instanceof Error;
}
function isCancelledError(value) {
  return value instanceof CancelledError;
}
function sleep(timeout) {
  return new Promise((resolve) => {
    setTimeout(resolve, timeout);
  });
}
function getStatusProps(status) {
  return {
    status,
    isLoading: status === QueryStatus.Loading,
    isSuccess: status === QueryStatus.Success,
    isError: status === QueryStatus.Error,
    isIdle: status === QueryStatus.Idle
  };
}
function createSetHandler(fn) {
  let removePreviousHandler;
  return (callback) => {
    if (removePreviousHandler) {
      removePreviousHandler();
    }
    removePreviousHandler = callback(fn);
  };
}

const defaultQueryKeySerializerFn = (queryKey) => {
  try {
    let arrayQueryKey = Array.isArray(queryKey) ? queryKey : [queryKey];
    const queryHash = stableStringify(arrayQueryKey);
    arrayQueryKey = JSON.parse(queryHash);
    return [queryHash, arrayQueryKey];
  } catch {
    throw new Error("A valid query key is required!");
  }
};
const DEFAULT_CONFIG = {
  queries: {
    queryKeySerializerFn: defaultQueryKeySerializerFn,
    enabled: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1e3 * 2 ** attemptIndex, 3e4),
    staleTime: 0,
    cacheTime: 5 * 60 * 1e3,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
    structuralSharing: true
  }
};
function mergeReactQueryConfigs(a, b) {
  return {
    shared: {
      ...a.shared,
      ...b.shared
    },
    queries: {
      ...a.queries,
      ...b.queries
    },
    mutations: {
      ...a.mutations,
      ...b.mutations
    }
  };
}
function getDefaultedQueryConfig(queryCacheConfig, contextConfig, config, configOverrides) {
  return {
    ...DEFAULT_CONFIG.shared,
    ...DEFAULT_CONFIG.queries,
    ...queryCacheConfig?.shared,
    ...queryCacheConfig?.queries,
    ...contextConfig?.shared,
    ...contextConfig?.queries,
    ...config,
    ...configOverrides
  };
}

class QueryObserver {
  constructor(config) {
    this.config = config;
    this.clear = this.clear.bind(this);
    this.refetch = this.refetch.bind(this);
    this.fetchMore = this.fetchMore.bind(this);
    this.updateQuery();
  }
  subscribe(listener) {
    this.started = true;
    this.updateListener = listener;
    this.currentQuery.subscribeObserver(this);
    this.optionalFetch();
    this.updateRefetchInterval();
    return this.unsubscribe.bind(this);
  }
  unsubscribe() {
    this.started = false;
    this.updateListener = void 0;
    this.clearRefetchInterval();
    this.currentQuery.unsubscribeObserver(this);
  }
  updateConfig(config) {
    const prevConfig = this.config;
    this.config = config;
    const updated = this.updateQuery();
    if (!this.started) {
      return;
    }
    if (updated) {
      this.optionalFetch();
      this.updateRefetchInterval();
      return;
    }
    if (config.enabled && !prevConfig.enabled) {
      this.optionalFetch();
    }
    if (config.enabled !== prevConfig.enabled || config.refetchInterval !== prevConfig.refetchInterval || config.refetchIntervalInBackground !== prevConfig.refetchIntervalInBackground) {
      this.updateRefetchInterval();
    }
  }
  getCurrentResult() {
    return this.currentResult;
  }
  clear() {
    return this.currentQuery.clear();
  }
  async refetch(options) {
    this.currentQuery.updateConfig(this.config);
    return this.currentQuery.refetch(options);
  }
  async fetchMore(fetchMoreVariable, options) {
    this.currentQuery.updateConfig(this.config);
    return this.currentQuery.fetchMore(fetchMoreVariable, options);
  }
  async fetch() {
    this.currentQuery.updateConfig(this.config);
    try {
      return await this.currentQuery.fetch();
    } catch (error) {
      return void 0;
    }
  }
  optionalFetch() {
    if (this.config.enabled && !(this.config.suspense && this.currentResult.isFetched) && this.currentResult.isStale && (this.config.refetchOnMount || this.currentQuery.observers.length === 1)) {
      this.fetch();
    }
  }
  updateRefetchInterval() {
    if (isServer) {
      return;
    }
    this.clearRefetchInterval();
    if (!this.config.enabled || !this.config.refetchInterval || this.config.refetchInterval < 0 || this.config.refetchInterval === Infinity) {
      return;
    }
    this.refetchIntervalId = setInterval(() => {
      if (this.config.refetchIntervalInBackground || isDocumentVisible()) {
        this.fetch();
      }
    }, this.config.refetchInterval);
  }
  clearRefetchInterval() {
    if (this.refetchIntervalId) {
      clearInterval(this.refetchIntervalId);
      this.refetchIntervalId = void 0;
    }
  }
  createResult() {
    const {currentQuery, previousResult, config} = this;
    const {
      canFetchMore,
      error,
      failureCount,
      isFetched,
      isFetching,
      isFetchingMore,
      isLoading,
      isStale
    } = currentQuery.state;
    let {data, status, updatedAt} = currentQuery.state;
    if (config.keepPreviousData && isLoading && previousResult?.isSuccess) {
      data = previousResult.data;
      updatedAt = previousResult.updatedAt;
      status = previousResult.status;
    }
    return {
      ...getStatusProps(status),
      canFetchMore,
      clear: this.clear,
      data,
      error,
      failureCount,
      fetchMore: this.fetchMore,
      isFetched,
      isFetching,
      isFetchingMore,
      isStale,
      query: currentQuery,
      refetch: this.refetch,
      updatedAt
    };
  }
  updateQuery() {
    const prevQuery = this.currentQuery;
    const config = prevQuery ? {...this.config, initialData: void 0} : this.config;
    const newQuery = config.queryCache.buildQuery(config.queryKey, config);
    if (newQuery === prevQuery) {
      return false;
    }
    newQuery.activateTimeouts();
    this.previousResult = this.currentResult;
    this.currentQuery = newQuery;
    this.currentResult = this.createResult();
    if (this.started) {
      prevQuery?.unsubscribeObserver(this);
      this.currentQuery.subscribeObserver(this);
    }
    return true;
  }
  onQueryUpdate(_state, action) {
    this.currentResult = this.createResult();
    const {data, error, isSuccess, isError} = this.currentResult;
    if (action.type === "Success" && isSuccess) {
      this.config.onSuccess?.(data);
      this.config.onSettled?.(data, null);
      this.updateRefetchInterval();
    } else if (action.type === "Error" && isError) {
      this.config.onError?.(error);
      this.config.onSettled?.(void 0, error);
      this.updateRefetchInterval();
    }
    this.updateListener?.(this.currentResult);
  }
}

var ActionType;
(function(ActionType2) {
  ActionType2["Failed"] = "Failed";
  ActionType2["MarkStale"] = "MarkStale";
  ActionType2["Fetch"] = "Fetch";
  ActionType2["Success"] = "Success";
  ActionType2["Error"] = "Error";
})(ActionType || (ActionType = {}));
class Query {
  constructor(init) {
    this.config = init.config;
    this.queryCache = init.queryCache;
    this.queryKey = init.queryKey;
    this.queryHash = init.queryHash;
    this.notifyGlobalListeners = init.notifyGlobalListeners;
    this.observers = [];
    this.state = getDefaultState(init.config);
    this.enableTimeouts = false;
  }
  activateTimeouts() {
    this.enableTimeouts = true;
    this.rescheduleStaleTimeout();
    this.rescheduleGarbageCollection();
  }
  updateConfig(config) {
    this.config = config;
  }
  dispatch(action) {
    this.state = queryReducer(this.state, action);
    this.observers.forEach((d) => d.onQueryUpdate(this.state, action));
    this.notifyGlobalListeners(this);
  }
  rescheduleStaleTimeout() {
    if (isServer) {
      return;
    }
    this.clearStaleTimeout();
    if (!this.enableTimeouts || this.state.isStale || this.state.status !== QueryStatus.Success || this.config.staleTime === Infinity) {
      return;
    }
    const staleTime = this.config.staleTime || 0;
    let timeout = staleTime;
    if (this.state.updatedAt) {
      const timeElapsed = Date.now() - this.state.updatedAt;
      const timeUntilStale = staleTime - timeElapsed;
      timeout = Math.max(timeUntilStale, 0);
    }
    this.staleTimeout = setTimeout(() => {
      this.invalidate();
    }, timeout);
  }
  invalidate() {
    this.clearStaleTimeout();
    if (this.state.isStale) {
      return;
    }
    this.dispatch({type: ActionType.MarkStale});
  }
  rescheduleGarbageCollection() {
    if (isServer) {
      return;
    }
    this.clearCacheTimeout();
    if (!this.enableTimeouts || this.config.cacheTime === Infinity || this.observers.length > 0) {
      return;
    }
    this.cacheTimeout = setTimeout(() => {
      this.clear();
    }, this.config.cacheTime);
  }
  async refetch(options) {
    try {
      return await this.fetch();
    } catch (error) {
      if (options?.throwOnError === true) {
        throw error;
      }
      return void 0;
    }
  }
  cancel() {
    this.cancelFetch?.();
  }
  continue() {
    this.continueFetch?.();
  }
  clearTimersObservers() {
    this.observers.forEach((observer) => {
      observer.clearRefetchInterval();
    });
  }
  clearStaleTimeout() {
    if (this.staleTimeout) {
      clearTimeout(this.staleTimeout);
      this.staleTimeout = void 0;
    }
  }
  clearCacheTimeout() {
    if (this.cacheTimeout) {
      clearTimeout(this.cacheTimeout);
      this.cacheTimeout = void 0;
    }
  }
  setData(updater) {
    const prevData = this.state.data;
    let data = functionalUpdate(updater, prevData);
    if (this.config.structuralSharing) {
      data = replaceEqualDeep(prevData, data);
    }
    if (this.config.isDataEqual?.(prevData, data)) {
      data = prevData;
    }
    const isStale = this.config.staleTime === 0;
    const canFetchMore = hasMorePages(this.config, data);
    this.dispatch({
      type: ActionType.Success,
      data,
      isStale,
      canFetchMore
    });
    this.rescheduleStaleTimeout();
  }
  clear() {
    this.clearStaleTimeout();
    this.clearCacheTimeout();
    this.clearTimersObservers();
    this.cancel();
    delete this.queryCache.queries[this.queryHash];
    this.notifyGlobalListeners(this);
  }
  isEnabled() {
    return this.observers.some((observer) => observer.config.enabled);
  }
  onWindowFocus() {
    if (this.state.isStale && this.observers.some((observer) => observer.config.enabled && observer.config.refetchOnWindowFocus)) {
      this.fetch();
    }
    this.continue();
  }
  onOnline() {
    if (this.state.isStale && this.observers.some((observer) => observer.config.enabled && observer.config.refetchOnReconnect)) {
      this.fetch();
    }
    this.continue();
  }
  subscribe(listener) {
    const observer = new QueryObserver({
      queryCache: this.queryCache,
      queryKey: this.queryKey,
      ...this.config
    });
    observer.subscribe(listener);
    return observer;
  }
  subscribeObserver(observer) {
    this.observers.push(observer);
    this.clearCacheTimeout();
  }
  unsubscribeObserver(observer) {
    this.observers = this.observers.filter((x) => x !== observer);
    if (!this.observers.length) {
      if (this.isTransportCancelable) {
        this.cancel();
      }
    }
    this.rescheduleGarbageCollection();
  }
  async tryFetchData(config, fn) {
    return new Promise((outerResolve, outerReject) => {
      let resolved = false;
      let continueLoop;
      let cancelTransport;
      const done = () => {
        resolved = true;
        delete this.cancelFetch;
        delete this.continueFetch;
        delete this.isTransportCancelable;
        continueLoop?.();
      };
      const resolve = (value) => {
        done();
        outerResolve(value);
      };
      const reject = (value) => {
        done();
        outerReject(value);
      };
      this.cancelFetch = () => {
        reject(new CancelledError());
        try {
          cancelTransport?.();
        } catch {
        }
      };
      this.continueFetch = () => {
        continueLoop?.();
      };
      const run = async () => {
        try {
          const promiseOrValue = fn();
          if (isCancelable(promiseOrValue)) {
            cancelTransport = () => {
              promiseOrValue.cancel();
            };
            this.isTransportCancelable = true;
          }
          resolve(await promiseOrValue);
        } catch (error) {
          if (resolved) {
            return;
          }
          const {failureCount} = this.state;
          const {retry, retryDelay} = config;
          const shouldRetry = retry === true || failureCount < retry || typeof retry === "function" && retry(failureCount, error);
          if (!shouldRetry) {
            reject(error);
            return;
          }
          this.dispatch({type: ActionType.Failed});
          await sleep(functionalUpdate(retryDelay, failureCount) || 0);
          if (!isDocumentVisible() || !isOnline()) {
            await new Promise((continueResolve) => {
              continueLoop = continueResolve;
            });
          }
          if (!resolved) {
            run();
          }
        }
      };
      run();
    });
  }
  async fetch(options) {
    if (this.promise) {
      return this.promise;
    }
    const config = this.config;
    if (!config.queryFn) {
      return;
    }
    const filter = config.queryFnParamsFilter;
    const params = filter ? filter(this.queryKey) : this.queryKey;
    this.promise = (async () => {
      try {
        let data;
        if (config.infinite) {
          data = await this.startInfiniteFetch(config, params, options);
        } else {
          data = await this.startFetch(config, params, options);
        }
        this.setData(data);
        delete this.promise;
        return data;
      } catch (error) {
        this.dispatch({
          type: ActionType.Error,
          error
        });
        if (!isCancelledError(error)) {
          Console.error(error);
        }
        delete this.promise;
        throw error;
      }
    })();
    return this.promise;
  }
  async startFetch(config, params, _options) {
    const fetchData = () => config.queryFn(...params);
    if (!this.state.isFetching) {
      this.dispatch({type: ActionType.Fetch});
    }
    return this.tryFetchData(config, fetchData);
  }
  async startInfiniteFetch(config, params, options) {
    const fetchMore = options?.fetchMore;
    const {previous, fetchMoreVariable} = fetchMore || {};
    const isFetchingMore = fetchMore ? previous ? "previous" : "next" : false;
    const prevPages = this.state.data || [];
    const fetchPage = async (pages, prepend, cursor) => {
      const lastPage = getLastPage(pages, prepend);
      if (typeof cursor === "undefined" && typeof lastPage !== "undefined" && config.getFetchMore) {
        cursor = config.getFetchMore(lastPage, pages);
      }
      const page = await config.queryFn(...params, cursor);
      return prepend ? [page, ...pages] : [...pages, page];
    };
    const fetchData = () => {
      if (isFetchingMore) {
        return fetchPage(prevPages, previous, fetchMoreVariable);
      } else if (!prevPages.length) {
        return fetchPage([]);
      } else {
        let promise = fetchPage([]);
        for (let i = 1; i < prevPages.length; i++) {
          promise = promise.then(fetchPage);
        }
        return promise;
      }
    };
    if (!this.state.isFetching) {
      this.dispatch({type: ActionType.Fetch, isFetchingMore});
    }
    return this.tryFetchData(config, fetchData);
  }
  fetchMore(fetchMoreVariable, options) {
    return this.fetch({
      fetchMore: {
        fetchMoreVariable,
        previous: options?.previous || false
      }
    });
  }
}
function getLastPage(pages, previous) {
  return previous ? pages[0] : pages[pages.length - 1];
}
function hasMorePages(config, pages, previous) {
  if (config.infinite && config.getFetchMore && Array.isArray(pages)) {
    return Boolean(config.getFetchMore(getLastPage(pages, previous), pages));
  }
  return void 0;
}
function getDefaultState(config) {
  const initialData = typeof config.initialData === "function" ? config.initialData() : config.initialData;
  const hasInitialData = typeof initialData !== "undefined";
  const isStale = !config.enabled || (typeof config.initialStale === "function" ? config.initialStale() : config.initialStale ?? !hasInitialData);
  const initialStatus = hasInitialData ? QueryStatus.Success : config.enabled ? QueryStatus.Loading : QueryStatus.Idle;
  return {
    ...getStatusProps(initialStatus),
    error: null,
    isFetched: false,
    isFetching: initialStatus === QueryStatus.Loading,
    isFetchingMore: false,
    failureCount: 0,
    isStale,
    data: initialData,
    updatedAt: hasInitialData ? Date.now() : 0,
    canFetchMore: hasMorePages(config, initialData)
  };
}
function queryReducer(state, action) {
  switch (action.type) {
    case ActionType.Failed:
      return {
        ...state,
        failureCount: state.failureCount + 1
      };
    case ActionType.MarkStale:
      return {
        ...state,
        isStale: true
      };
    case ActionType.Fetch:
      const status = typeof state.data !== "undefined" ? QueryStatus.Success : QueryStatus.Loading;
      return {
        ...state,
        ...getStatusProps(status),
        isFetching: true,
        isFetchingMore: action.isFetchingMore || false,
        failureCount: 0
      };
    case ActionType.Success:
      return {
        ...state,
        ...getStatusProps(QueryStatus.Success),
        data: action.data,
        error: null,
        isStale: action.isStale,
        isFetched: true,
        isFetching: false,
        isFetchingMore: false,
        canFetchMore: action.canFetchMore,
        updatedAt: Date.now(),
        failureCount: 0
      };
    case ActionType.Error:
      return {
        ...state,
        ...getStatusProps(QueryStatus.Error),
        error: action.error,
        isFetched: true,
        isFetching: false,
        isFetchingMore: false,
        isStale: true,
        failureCount: state.failureCount + 1,
        throwInErrorBoundary: true
      };
    default:
      return state;
  }
}

class QueryCache {
  constructor(config2) {
    this.config = config2 || {};
    this.globalListeners = [];
    this.queries = {};
    this.isFetching = 0;
  }
  notifyGlobalListeners(query2) {
    this.isFetching = Object.values(this.queries).reduce((acc, query3) => query3.state.isFetching ? acc + 1 : acc, 0);
    this.globalListeners.forEach((d) => d(this, query2));
  }
  getDefaultConfig() {
    return this.config.defaultConfig;
  }
  getDefaultedQueryConfig(config2) {
    return getDefaultedQueryConfig(this.getDefaultConfig(), void 0, config2, {
      queryCache: this
    });
  }
  subscribe(listener) {
    this.globalListeners.push(listener);
    return () => {
      this.globalListeners.splice(this.globalListeners.indexOf(listener), 1);
    };
  }
  clear(options) {
    Object.values(this.queries).forEach((query2) => query2.clear());
    this.queries = {};
    if (options?.notify) {
      this.notifyGlobalListeners();
    }
  }
  getQueries(predicate, options) {
    if (predicate === true) {
      return Object.values(this.queries);
    }
    let predicateFn;
    if (typeof predicate === "function") {
      predicateFn = predicate;
    } else {
      const config2 = this.getDefaultedQueryConfig();
      const [queryHash, queryKey] = config2.queryKeySerializerFn(predicate);
      predicateFn = (d) => options?.exact ? d.queryHash === queryHash : deepIncludes(d.queryKey, queryKey);
    }
    return Object.values(this.queries).filter(predicateFn);
  }
  getQuery(predicate) {
    return this.getQueries(predicate, {exact: true})[0];
  }
  getQueryData(predicate) {
    return this.getQuery(predicate)?.state.data;
  }
  removeQueries(predicate, options) {
    this.getQueries(predicate, options).forEach((query2) => query2.clear());
  }
  cancelQueries(predicate, options) {
    this.getQueries(predicate, options).forEach((query2) => query2.cancel());
  }
  async invalidateQueries(predicate, options) {
    const {refetchActive = true, refetchInactive = false, throwOnError} = options || {};
    try {
      await Promise.all(this.getQueries(predicate, options).map((query2) => {
        if (query2.observers.length) {
          if (refetchActive && query2.isEnabled()) {
            return query2.fetch();
          }
        } else {
          if (refetchInactive) {
            return query2.fetch();
          }
        }
        return query2.invalidate();
      }));
    } catch (err) {
      if (throwOnError) {
        throw err;
      }
    }
  }
  resetErrorBoundaries() {
    this.getQueries(true).forEach((query2) => {
      query2.state.throwInErrorBoundary = false;
    });
  }
  buildQuery(userQueryKey, queryConfig) {
    const config2 = this.getDefaultedQueryConfig(queryConfig);
    const [queryHash, queryKey] = config2.queryKeySerializerFn(userQueryKey);
    let query2;
    if (this.queries[queryHash]) {
      query2 = this.queries[queryHash];
      query2.updateConfig(config2);
    }
    if (!query2) {
      query2 = new Query({
        queryCache: this,
        queryKey,
        queryHash,
        config: config2,
        notifyGlobalListeners: (query3) => {
          this.notifyGlobalListeners(query3);
        }
      });
      if (!this.config.frozen) {
        this.queries[queryHash] = query2;
        this.notifyGlobalListeners(query2);
      }
    }
    return query2;
  }
  async prefetchQuery(...args) {
    if (isObject(args[1]) && (args[1].hasOwnProperty("throwOnError") || args[1].hasOwnProperty("force"))) {
      args[3] = args[1];
      args[1] = void 0;
      args[2] = void 0;
    }
    const [queryKey, config2, options] = getQueryArgs(args);
    const configWithoutRetry = {retry: false, ...config2};
    let query2;
    try {
      query2 = this.buildQuery(queryKey, configWithoutRetry);
      if (options?.force || query2.state.isStale) {
        await query2.fetch();
      }
      return query2.state.data;
    } catch (error) {
      if (options?.throwOnError) {
        throw error;
      }
      return;
    } finally {
      if (query2) {
        query2.activateTimeouts();
      }
    }
  }
  setQueryData(queryKey, updater, config2) {
    const query2 = this.getQuery(queryKey);
    if (query2) {
      query2.setData(updater);
      return;
    }
    const newQuery = this.buildQuery(queryKey, {
      initialStale: typeof config2?.staleTime === "undefined",
      initialData: functionalUpdate(updater, void 0),
      ...config2
    });
    newQuery.activateTimeouts();
  }
}
const defaultQueryCache = makeQueryCache({frozen: isServer});
const queryCaches = [defaultQueryCache];
function makeQueryCache(config2) {
  return new QueryCache(config2);
}
function onVisibilityOrOnlineChange(isOnlineChange) {
  if (isDocumentVisible() && isOnline()) {
    queryCaches.forEach((queryCache) => {
      queryCache.getQueries((query2) => {
        if (isOnlineChange) {
          query2.onOnline();
        } else {
          query2.onWindowFocus();
        }
      });
    });
  }
}

const setFocusHandler = createSetHandler(() => onVisibilityOrOnlineChange(false));
setFocusHandler((handleFocus) => {
  if (isServer || !window?.addEventListener) {
    return;
  }
  window.addEventListener("visibilitychange", handleFocus, false);
  window.addEventListener("focus", handleFocus, false);
  return () => {
    window.removeEventListener("visibilitychange", handleFocus);
    window.removeEventListener("focus", handleFocus);
  };
});

const setOnlineHandler = createSetHandler(() => onVisibilityOrOnlineChange(true));
setOnlineHandler((handleOnline) => {
  if (isServer || !window?.addEventListener) {
    return;
  }
  window.addEventListener("online", handleOnline, false);
  return () => {
    window.removeEventListener("online", handleOnline);
  };
});

let queryConfigContext = "__svelte_query_config";
let queryCacheContext = "__svelte_query_cache";
function getQueryCache() {
  return getContext(queryCacheContext) || defaultQueryCache;
}
function getQueryConfig() {
  return getContext(queryConfigContext);
}

/* lib/components/CacheProvider.svelte generated by Svelte v3.24.1 */

function create_fragment(ctx) {
	let current;
	const default_slot_template = /*$$slots*/ ctx[2].default;
	const default_slot = create_slot(default_slot_template, ctx, /*$$scope*/ ctx[1], null);

	return {
		c() {
			if (default_slot) default_slot.c();
		},
		m(target, anchor) {
			if (default_slot) {
				default_slot.m(target, anchor);
			}

			current = true;
		},
		p(ctx, [dirty]) {
			if (default_slot) {
				if (default_slot.p && dirty & /*$$scope*/ 2) {
					update_slot(default_slot, default_slot_template, ctx, /*$$scope*/ ctx[1], dirty, null, null);
				}
			}
		},
		i(local) {
			if (current) return;
			transition_in(default_slot, local);
			current = true;
		},
		o(local) {
			transition_out(default_slot, local);
			current = false;
		},
		d(detaching) {
			if (default_slot) default_slot.d(detaching);
		}
	};
}

function instance($$self, $$props, $$invalidate) {
	let { queryCache } = $$props;
	let resolvedQueryCache = queryCache || makeQueryCache();
	setContext(queryCacheContext, resolvedQueryCache);

	onMount(() => {
		queryCaches.push(resolvedQueryCache);

		return () => {
			let i = queryCaches.indexOf(resolvedQueryCache);

			if (i > -1) {
				queryCaches.splice(i, 1);
			}

			if (!queryCache) {
				resolvedQueryCache.clear({ notify: false });
			}
		};
	});

	let { $$slots = {}, $$scope } = $$props;

	$$self.$$set = $$props => {
		if ("queryCache" in $$props) $$invalidate(0, queryCache = $$props.queryCache);
		if ("$$scope" in $$props) $$invalidate(1, $$scope = $$props.$$scope);
	};

	return [queryCache, $$scope, $$slots];
}

class CacheProvider extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance, create_fragment, not_equal, { queryCache: 0 });
	}
}

/* lib/components/ConfigProvider.svelte generated by Svelte v3.24.1 */

function create_fragment$1(ctx) {
	let current;
	const default_slot_template = /*$$slots*/ ctx[2].default;
	const default_slot = create_slot(default_slot_template, ctx, /*$$scope*/ ctx[1], null);

	return {
		c() {
			if (default_slot) default_slot.c();
		},
		m(target, anchor) {
			if (default_slot) {
				default_slot.m(target, anchor);
			}

			current = true;
		},
		p(ctx, [dirty]) {
			if (default_slot) {
				if (default_slot.p && dirty & /*$$scope*/ 2) {
					update_slot(default_slot, default_slot_template, ctx, /*$$scope*/ ctx[1], dirty, null, null);
				}
			}
		},
		i(local) {
			if (current) return;
			transition_in(default_slot, local);
			current = true;
		},
		o(local) {
			transition_out(default_slot, local);
			current = false;
		},
		d(detaching) {
			if (default_slot) default_slot.d(detaching);
		}
	};
}

function instance$1($$self, $$props, $$invalidate) {
	let { config } = $$props;
	let prevConfig = getQueryConfig();

	let newConfig = prevConfig
	? mergeReactQueryConfigs(prevConfig, config)
	: config;

	setContext(queryConfigContext, newConfig);
	let { $$slots = {}, $$scope } = $$props;

	$$self.$$set = $$props => {
		if ("config" in $$props) $$invalidate(0, config = $$props.config);
		if ("$$scope" in $$props) $$invalidate(1, $$scope = $$props.$$scope);
	};

	return [config, $$scope, $$slots];
}

class ConfigProvider extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$1, create_fragment$1, not_equal, { config: 0 });
	}
}

let isFetching = {
  subscribe(listener) {
    function notify(value) {
      try {
        listener(value);
      } catch {
      }
    }
    let queryCache = getQueryCache();
    let prev = queryCache.isFetching;
    notify(prev);
    return queryCache.subscribe((cache) => {
      let curr = cache.isFetching;
      if (curr !== prev) {
        notify(curr);
        prev = curr;
      }
    });
  }
};

function useDefaultedQueryConfig(config2) {
  let contextConfig = getQueryConfig();
  let contextQueryCache = getQueryCache();
  let queryCache = config2?.queryCache || contextQueryCache;
  let queryCacheConfig = queryCache.getDefaultConfig();
  return getDefaultedQueryConfig(queryCacheConfig, contextConfig, config2, {
    queryCache
  });
}

function baseQuery(config = {}) {
  config = useDefaultedQueryConfig(config);
  let observer = null;
  let {subscribe} = readable({}, (set) => {
    if (!observer)
      observer = new QueryObserver(config);
    set(observer.getCurrentResult());
    return observer.subscribe(set);
  });
  function configure(newConfig) {
    config = useDefaultedQueryConfig({...config, ...newConfig});
    observer?.updateConfig(config);
  }
  function refetch(key) {
    configure({queryKey: key});
  }
  return {subscribe, configure, refetch};
}

function query(...args) {
  let conf = getQueryArgs(args)[1];
  return baseQuery(conf);
}

function infiniteQuery(...args) {
  let conf = getQueryArgs(args)[1];
  return baseQuery({...conf, infinite: true});
}

function paginatedQuery(...args) {
  let conf = getQueryArgs(args)[1];
  let base = baseQuery({...conf, keepPreviousData: true});
  let {subscribe} = derived(base, ($result) => ({
    ...$result,
    resolvedData: $result.data,
    latestData: $result.query.state.data === $result.data ? $result.data : void 0
  }));
  return {...base, subscribe};
}

export { CancelledError, CacheProvider as QueryCacheProvider, ConfigProvider as QueryConfigProvider, QueryStatus, getQueryCache, infiniteQuery, isCancelledError, isError, isFetching, makeQueryCache, paginatedQuery, query, defaultQueryCache as queryCache, queryCaches, setConsole, setFocusHandler, setOnlineHandler };
